/**
 * 人机边界的单位换算 —— Y-59(2026-10-26 人裁决)的唯一落点。
 *
 * 规矩一条:**人看到和填的是友好单位,契约 / 库 / 服务端计算不动。**
 *   重量 kg(3 位小数 = 1 g)· 金额 元(2 位小数 = 1 分)· 体积 m³(6 位小数 = 1 cm³)
 *   佣金率百分数(bp 的展示走 money.ts 的 formatRateBp)
 *
 * 为什么用字符串做十进制 → 整数,而不是 parseFloat 之后 `* 1000`:
 * `parseFloat('2.5') * 1000` 在某些数上不是 2500,而是 2499.9999999999995,
 * 再取整就是 2499 —— 少 1 克、少 1 分,没有任何东西会报。金额与重量上的
 * 「静默少一档」正是 M-14 与 Y-59 要消灭的那一类事故。
 *
 * 为什么超精度一律拒绝、不四舍五入:填 2.5001 kg 的人以为自己精确到了 0.1 克,
 * 四舍五入会把它变成 2500 克 —— 静默抹掉的那一位在重量和钱上都无人看得见。
 * 拒绝会让人当场改,抹位不会。
 *
 * 注意:下面两个承重函数在 guards/check-friendly-units.mjs 里被抽出函数体求值
 * (函数体只写普通 JS,没有 TS 断言、不引用模块内其它符号),改它们时要一起看那张行为表。
 */

/** 1 千克 = 1000 克 */
export const GRAM_PER_KG = 1000
/** 1 元 = 100 分 */
export const FEN_PER_YUAN = 100
/** 1 立方米 = 1,000,000 立方厘米 */
export const CM3_PER_M3 = 1_000_000

/**
 * 十进制字符串 × 10^scale → 整数。转不了返回 null(不抛、不猜)。
 *
 * 合法:`'2.5'`(scale 3)→ 2500、`'0.001'` → 1、`'0'` → 0
 * 拒绝:空串 / 负号 / 加号 / 科学计数 / 多余小数点 / 小数位超过 scale
 */
export function parseScaledDecimal(input: string, scale: number): number | null {
  const text = String(input).trim()
  if (!/^\d+(\.\d+)?$/.test(text)) return null
  const dot = text.indexOf('.')
  const whole = dot === -1 ? text : text.slice(0, dot)
  const frac = dot === -1 ? '' : text.slice(dot + 1)
  if (frac.length > scale) return null
  const digits = whole + frac.padEnd(scale, '0')
  const value = Number(digits)
  if (!Number.isSafeInteger(value)) return null
  return value
}

/**
 * 整数 × 10^-scale → 去掉尾零的十进制字符串(不补零、不带单位)。
 *
 * `2500,3` → `'2.5'`、`1,3` → `'0.001'`、`100000000,6` → `'100'`、`0,2` → `'0'`
 *
 * 非负安全整数之外的值直接抛:展示函数拿到一个不该存在的数时,
 * 编一个像样的字符串比报错更危险 —— 它会让一个坏值看起来完全正常。
 */
export function formatScaledDecimal(value: number, scale: number): string {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`要展示的值必须是非负安全整数,实际拿到 ${JSON.stringify(value)}`)
  }
  const digits = String(value).padStart(scale + 1, '0')
  const whole = digits.slice(0, digits.length - scale)
  const frac = digits.slice(digits.length - scale).replace(/0+$/, '')
  return frac === '' ? whole : `${whole}.${frac}`
}

// ───────────────────────────────────────────────────────
// 语义包装:调用方读自己的单位名,不读 scale 数字
//
// 下面六个都只是「scale 的那一次取值」—— 单点定义在这里,是因为
// 同一条换算散落在三个端的七个文件里时,迟早有一处写成 2 或 6 而没人发现。
// ───────────────────────────────────────────────────────

/** kg(最多 3 位小数)→ 克 */
export function parseKgToGrams(input: string): number | null {
  return parseScaledDecimal(input, 3)
}

/** 克 → kg 字符串 */
export function formatGramsAsKg(value: number): string {
  return formatScaledDecimal(value, 3)
}

/** 元(最多 2 位小数)→ 分 */
export function parseYuanToFen(input: string): number | null {
  return parseScaledDecimal(input, 2)
}

/** 分 → 元字符串(不带 ¥ 与千分位;输入框与计价档位文案用) */
export function formatFenAsYuan(value: number): string {
  return formatScaledDecimal(value, 2)
}

/**
 * 整数输入(最多 0 位小数)→ 整数。`'12'` → 12、`'0'` → 0、`'12.5'` → null。
 *
 * 为什么单独开一个名字:输入框以前用 `:precision="0"` 把 12.5 静默四舍五入成 13 ——
 * 人以为自己填的是 12.5,库里却是 13,而页面上没有任何变化。这里明确拒绝,
 * 让运营当场改。函数体只写普通 JS(守卫会抽出来求值)。
 */
export function parseWholeNumber(input: string): number | null {
  return parseScaledDecimal(input, 0)
}

/** m³(最多 6 位小数)→ 立方厘米 */
export function parseM3ToCm3(input: string): number | null {
  return parseScaledDecimal(input, 6)
}

/** 立方厘米 → m³ 字符串 */
export function formatCm3AsM3(value: number): string {
  return formatScaledDecimal(value, 6)
}
